import React, { useState } from "react"
import Navbar from "../../components/Navbar/Navbar"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  Row,
  Col,
  TimePicker,
  DatePicker,
  Select,
  Checkbox,
  Space,
} from "antd"
import Title from "antd/es/typography/Title"
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons"
import { CSVLink } from "react-csv"
import { usePostRequest } from "../../hooks/usePostRequest"
import { UseGetRequest, UsePostRequest } from "../../utils/api/ApiRequests"
import moment from "moment"
import dayjs from "dayjs"
import {
  capitalizeEachWord,
  extractDate,
  getIsAdminLogin,
  getEmployeeId,
  minsToHours,
  formatShortDate,
} from "../../utils/Common"
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi"

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false)
  const values = Form.useWatch([], form)
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true)
        },
        () => {
          setSubmittable(false)
        }
      )
  }, [values])

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  )
}

export default function Timesheet() {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const employeeOptions = GetAllEmployees()
  const isAdminLogin = getIsAdminLogin()
  const employeeId = getEmployeeId()
  const dateFormat = "YYYY-MM-DD"
  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowViewModal(false)
    setShowConfirmDelete(false)
    setShowConfirmMultipleDelete(false)
    resetFields()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  })
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [clickedRowData, setClickedRowData] = useState({})
  const [tableData, setTableData] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [modal, contextHolder] = Modal.useModal()
  const [showDeleteMultiple, setShowDeleteMultiple] = useState(false)
  const [itemsToDelete, setItemsToDelete] = useState([])
  const [employeeProjects, setEmployeeProjects] = useState([])
  const [projectDetails, setProjectDetails] = useState({})
  const [reason, setReason] = useState("")
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] =
    useState(false)
  const [disableTime2, setDisableTime2] = useState(false)
  const [excelUrls, setExcelUrls] = useState({
    daily: null,
    monthly: null,
    manpower: null,
  })

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    employee_id: isAdminLogin ? "" : employeeId,
    date_from: moment().format(dateFormat),
    date_to: moment().format(dateFormat),
  })

  const [isFetching, setIsFetching] = useState(false)

  const tableColumns = [
    {
      title: "",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (id) => (
        <div>
          {showDeleteMultiple && (
            <Checkbox
              onChange={(e) =>
                e.target.checked
                  ? setItemsToDelete([...itemsToDelete, id])
                  : setItemsToDelete(
                      itemsToDelete.filter((item) => item !== id)
                    )
              }
            />
          )}
        </div>
      ),
    },
    {
      title: "DTR ID",
      dataIndex: "dtr_id",
      key: "dtr_id",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {capitalizeEachWord(data)}
        </div>
      ),
    },
    {
      title: "Code",
      dataIndex: "project_id",
      key: "project_id",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    {
      title: "Work Date",
      dataIndex: "work_date",
      key: "work_date",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {moment(data).format("MMM DD, YYYY")}
        </div>
      ),
    },
    {
      title: "Time In 1",
      dataIndex: "time_in1",
      key: "time_in",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data !== "" ? moment(data, "hh:mm:ss").format("hh:mm A") : ""}
        </div>
      ),
    },
    {
      title: "Time Out 1",
      dataIndex: "time_out1",
      key: "time_out",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data ? moment(data, "hh:mm:ss").format("hh:mm A") : ""}
        </div>
      ),
    },
    {
      title: "Time In 2",
      dataIndex: "time_in2",
      key: "time_in",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data ? moment(data, "hh:mm:ss").format("hh:mm A") : ""}
        </div>
      ),
    },
    {
      title: "Time Out 2",
      dataIndex: "time_out2",
      key: "time_out",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data ? moment(data, "hh:mm:ss").format("hh:mm A") : ""}
        </div>
      ),
    },
    {
      title: "Late (hrs)",
      dataIndex: "late",
      key: "late  ",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    {
      title: "Undertime (hrs)",
      dataIndex: "undertime_hr",
      key: "undertime_hr",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    {
      title: "Reg OT (hrs)",
      dataIndex: "regular_ot_hr",
      key: "regular_ot_hr",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    {
      title: "Restday OT (hrs)",
      dataIndex: "restday_ot_hr",
      key: "restday_ot_hr",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data}
        </div>
      ),
    },
    // {
    //     title: 'Actions',
    //     width: '10%',
    //     render: (_, record) => (
    //       <Space size="middle">
    //          {/* <Button type="text" onClick={()=>{setInitialValues(()=>{return{...record}}); setAddCategoryModal(true);}} icon={<EditOutlined/>}>Edit</Button> */}
    //          <Button type="text" onClick={()=>{setInitialValues(()=>{return{...record}}); setEditFileList(()=>{return[{url: record.file_url}]}); setEditCategoryModal(true)}} icon={<EditOutlined/>}>Edit</Button>
    //          <Button type="text" danger onClick={()=>confirm(record)} icon={<DeleteOutlined/>}>Delete</Button>
    //       </Space>
    //     ),
    // },
  ]

  const reportHeaders = [
    { label: "DTR ID", key: "dtr_id" },
    { label: "Employee", key: "employee_name" },
    { label: "Code", key: "project_id" },
    { label: "Date", key: "formatted_work_date" },
    { label: "Time In 1", key: "time_in1" },
    { label: "Time Out 1", key: "time_out1" },
    { label: "Time In 2", key: "time_in2" },
    { label: "Time Out 2", key: "time_out2" },
    { label: "Late (hrs)", key: "late" },
    { label: "Undertime (hrs)", key: "undertime_hr" },
    { label: "Reg OT (hrs)", key: "regular_ot_hr" },
    { label: "Restday OT (hrs)", key: "restday_ot_hr" },
  ]

  const onFinish = (formInputs) => {
    formInputs.time_in1 = formInputs.time_in1.format("HH:mm:ss")
    formInputs.time_out1 = formInputs.time_out1.format("HH:mm:ss")
    formInputs.time_in2 = formInputs.time_in2
      ? formInputs.time_in2.format("HH:mm:ss")
      : null
    formInputs.time_out2 = formInputs.time_out2
      ? formInputs.time_out2.format("HH:mm:ss")
      : null
    formInputs.work_date = extractDate(formInputs.work_date)
    formInputs.is_holiday = formInputs.is_holiday ? "1" : "0"
    callPost("timesheets/create", formInputs, false)
  }

  const onEdit = (formInputs) => {
    formInputs.time_in1 = formInputs.time_in1.format("HH:mm:ss")
    formInputs.time_out1 = formInputs.time_out1.format("HH:mm:ss")
    formInputs.time_in2 = formInputs.time_in2
      ? formInputs.time_in2.format("HH:mm:ss")
      : null
    formInputs.time_out2 = formInputs.time_out2
      ? formInputs.time_out2.format("HH:mm:ss")
      : null
    formInputs.timesheet_id = clickedRowData.id
    formInputs.work_date = extractDate(formInputs.work_date)
    formInputs.is_holiday = formInputs.is_holiday ? "1" : "0"
    callPost(`timesheets/update`, formInputs, false)
  }

  function handleMultipleDelete() {
    itemsToDelete.forEach((item) => {
      handleDelete(item)
    })
  }

  // const confirmMultipleDelete = (e) => {
  //   modal.confirm({
  //     title: `Are you sure you want to delete the selected timesheets?`,
  //     icon: <ExclamationCircleOutlined />,
  //     content: "You cannot undo this action.",
  //     okText: "Delete",
  //     cancelText: "Cancel",
  //     onOk: () => handleMultipleDelete(),
  //     okType: "danger",
  //   });
  // };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }))
    }
  }

  function handleDelete(passedId) {
    callPost(
      "timesheets/delete",
      { timesheet_id: passedId, remarks: reason },
      false
    )
  }

  const handleRowClick = (rowData) => {
    setClickedRowData(rowData)
    const formattedValues = { ...rowData }
    fetchProjectDetails(rowData.project_id)
    formattedValues.work_date = rowData.work_date
      ? dayjs(rowData.work_date, dateFormat)
      : ""
    formattedValues.time_in1 = rowData.time_in1
      ? moment(rowData.time_in1, "hh:mm A")
      : ""
    formattedValues.time_out1 = rowData.time_out1
      ? moment(rowData.time_out1, "hh:mm A")
      : ""
    formattedValues.time_in2 = rowData.time_in2
      ? moment(rowData.time_in2, "hh:mm A")
      : ""
    formattedValues.time_out2 = rowData.time_out2
      ? moment(rowData.time_out2, "hh:mm A")
      : ""
    formattedValues.is_holiday = rowData.is_holiday === "1" ? true : false
    form.setFieldsValue(formattedValues)
    setShowViewModal(true)
  }

  const handleDateFilter = (e) => {
    if (e) {
      setFilterConfig((prev) => ({
        ...prev,
        date_from: e[0].format(dateFormat),
        date_to: e[1].format(dateFormat),
      }))
    }
  }

  const handleFormChange = (changedValues, allValues) => {
    if (allValues.employee_id) {
      fetchEmployeeProjects(allValues.employee_id)
      const empRow = employeeOptions.filter(
        (emp) => emp.id === allValues.employee_id
      )
      form.setFieldValue("dtr_id", empRow[0].dtr_id)
    }
  }

  const actionDropdownOptions = [
    {
      value: "generate",
      label: "Add Timesheet",
      action: () => setShowAddModal(true),
    },
    {
      value: "upload",
      label: "Upload Timesheet",
      action: () => navigate("upload", { state: "timesheet" }),
    },
    {
      value: "export",
      label: (
        <CSVLink
          className="no-style-btn"
          data={tableData}
          headers={reportHeaders}
          filename={"Timesheet.csv"}
        >
          Export Timesheet
        </CSVLink>
      ),
    },
    {
      value: "daily",
      label: (
        <a href={excelUrls.daily} className="pointer no-style-btn">
          Daily Report
        </a>
      ),
    },
    {
      value: "monthly",
      label: (
        <a href={excelUrls.monthly} className="pointer no-style-btn">
          Monthly Report
        </a>
      ),
    },
    {
      value: "manpower",
      label: (
        <a href={excelUrls.manpower} className="pointer no-style-btn">
          Manpower Report
        </a>
      ),
    },
    {
      value: "delete",
      label: "Delete Multiple",
      action: () => setShowDeleteMultiple(true),
    },
  ]

  function resetFields() {
    form.resetFields()
    setProjectDetails({})
    setDisableTime2(false)
    setReason("")
  }

  async function fetchEmployeeProjects(empId) {
    setEmployeeProjects([])
    const response = await UseGetRequest("employees/get_projects", {
      employee_id: empId,
    })
    if (response.data) {
      const res = (response.data.employee_projects ?? []).map((item) => {
        return {
          ...item,
          label: item.project,
          value: item.project_id,
        }
      })
      setEmployeeProjects(res)
    }
  }

  async function fetchProjectDetails(id) {
    setEmployeeProjects([])
    const response = await UseGetRequest("projects/get", { project_id: id })
    if (response.data) {
      const projectOptions = response.data.data.map((row) => {
        return {
          value: row.id,
          label: row.name,
        }
      })
      setEmployeeProjects(projectOptions)
      setProjectDetails({
        ...response.data.data[0],
        project_id: response.data.data[0].id,
      })
    }
  }

  function renderSecondTimes() {
    return (
      <>
        <Col xs={24} lg={12}>
          <Form.Item
            name="time_in2"
            label="Time In 2"
            rules={[
              {
                required: disableTime2,
              },
            ]}
          >
            <TimePicker format="hh:mm A" />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            name="time_out2"
            label="Time Out 2"
            rules={[
              {
                required: disableTime2,
              },
            ]}
          >
            <TimePicker format={"hh:mm A"} />
          </Form.Item>
        </Col>
      </>
    )
  }

  async function fetchExcelReportUrls() {
    const payload = {
      date_from: filterConfig.date_from,
      date_to: filterConfig.date_to,
    }

    const dailyReport = await UseGetRequest(
      "timesheets/create_timesheet_report_excel",
      { ...payload, salary_type: "daily6" }
    )
    if (dailyReport.data) {
      setExcelUrls((prev) => ({ ...prev, daily: dailyReport.data.url }))
    }

    const monthlyReport = await UseGetRequest(
      "timesheets/create_timesheet_report_excel",
      { ...payload, salary_type: "monthly26" }
    )
    if (monthlyReport.data) {
      setExcelUrls((prev) => ({ ...prev, monthly: monthlyReport.data.url }))
    }

    const manPowerReport = await UseGetRequest(
      "timesheets/create_timesheet_report_excel",
      payload
    )
    if (manPowerReport.data) {
      setExcelUrls((prev) => ({ ...prev, manpower: manPowerReport.data.url }))
    }
  }

  React.useEffect(() => {
    setItemsToDelete([])
    setShowDeleteMultiple(false)
    async function fetchTableData() {
      setIsFetching(true)
      setTableData([])
      const response = await UsePostRequest("timesheets/get", filterConfig)
      if (response.data) {
        const res = response.data.map((data) => {
          let info = data
          info.time_in1 = info.time_in1 === "00:00:00" ? "" : info.time_in1
          info.time_out1 = info.time_out1 === "00:00:00" ? "" : info.time_out1
          info.time_in2 = info.time_in2 === "00:00:00" ? "" : info.time_in2
          info.time_out2 = info.time_out2 === "00:00:00" ? "" : info.time_out2
          info.formatted_work_date = formatShortDate(data.work_date)
          info.late_hrs = minsToHours(data.late)
          info.undertime_hrs = minsToHours(data.undertime_mins)
          info.regular_ot_hrs = minsToHours(data.regular_ot_mins)
          info.restday_ot_hrs = minsToHours(data.restday_ot_mins)
          return info
        })
        setTableData(res)
      }
      setIsFetching(false)
    }

    fetchTableData()
    fetchExcelReportUrls()
  }, [filterConfig])

  React.useEffect(() => {
    if (
      projectDetails?.second_timein &&
      projectDetails?.second_timein.trim() !== ""
    ) {
      setDisableTime2(true)
    } else {
      setDisableTime2(false)
    }
  }, [projectDetails])

  React.useEffect(() => {
    renderSecondTimes()
  }, [disableTime2])

  return (
    <Navbar
      activeNav="timesheet"
      pageTitle="Timesheets"
      noButton
      actionDropdownButton
      actionDropdownOptions={actionDropdownOptions}
      onSearch={handleSearch}
      setSearchQuery={(e) => setSearchTerm(e)}
      searchPlaceholder="employee"
      isManager
      searchable={isAdminLogin}
      withDateRangePicker
      onDateChange={handleDateFilter}
    >
      {isAdminLogin && (
        <Row className="mt-4 d-flex justify-content-between">
          <Space className=""></Space>
          {showDeleteMultiple && (
            <Space className="">
              <Button
                danger
                // onClick={(e) => confirmMultipleDelete(e)}
                onClick={() => setShowConfirmMultipleDelete(true)}
                className="me-2"
                disabled={!itemsToDelete || itemsToDelete.length === 0}
              >
                Delete Selected
              </Button>
              <Button
                onClick={() => {
                  setShowDeleteMultiple(false)
                  setItemsToDelete([])
                }}
              >
                Cancel
              </Button>
            </Space>
          )}
        </Row>
      )}

      <Table
        className={isAdminLogin ? "" : "mt-4"}
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching || isLoading}
      />

      <Modal
        maskClosable={false}
        title={`${showAddModal ? "Add" : "Edit"} Timesheet`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false)
          setShowEditModal(false)
          resetFields()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
          onValuesChange={handleFormChange}
          initialValues={{ is_holiday: false }}
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select
                  options={employeeOptions.slice(1)}
                  onChange={() => {
                    form.setFieldValue("project_id", null)
                    setProjectDetails(null)
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    option.label?.toLowerCase().includes(input?.toLowerCase())
                  }
                  disabled={showEditModal}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="project_id"
                label="Project"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select
                  options={employeeProjects}
                  disabled={showEditModal}
                  onChange={(e, obj) => {
                    setProjectDetails(obj)
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} lg={10}>
              <Form.Item
                name="dtr_id"
                label="DTR ID"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={10}>
              <Form.Item
                name="work_date"
                label="Work Date"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={4}>
              <Form.Item name="is_holiday" label="" valuePropName="checked">
                <Checkbox className="ms-2 mt-4">Holiday</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="time_in1"
                label="Time In 1"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TimePicker format="hh:mm A" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="time_out1"
                label="Time Out 1"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TimePicker format={"hh:mm A"} />
              </Form.Item>
            </Col>
            {disableTime2 && renderSecondTimes()}

            <Col xs={24} lg={12} className="default-bg p-2">
              <Row className="bold primary-color">
                {projectDetails?.project} Schedule
              </Row>
              {projectDetails && projectDetails.project_id && (
                <div className="">
                  <Row className="d-flex justify-content-between ms-3">
                    <Col className="smaller-text">
                      Time In 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timein,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                    <Col className="smaller-text">
                      Time Out 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timeout,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-between ms-3">
                    {projectDetails.second_timein && (
                      <Col className="smaller-text">
                        Time In 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timein,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                    {projectDetails.second_timeout && (
                      <Col className="smaller-text">
                        Time Out 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timeout,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    resetFields()
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Timesheet"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false)
          resetFields()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={employeeOptions.slice(1)} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="project_id"
                label="Project"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={employeeProjects} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} lg={10}>
              <Form.Item
                name="dtr_id"
                label="DTR ID"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={10}>
              <Form.Item name="work_date" label="Work Date">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={4}>
              <Form.Item name="is_holiday" label="" valuePropName="checked">
                <Checkbox className="ms-2 mt-4" disabled>
                  Holiday
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="time_in1" label="Time In 1">
                <TimePicker format={"hh:mm A"} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="time_out1" label="Time Out 1">
                <TimePicker format={"hh:mm A"} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="time_in2"
                label="Time In 2"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <TimePicker format={"hh:mm A"} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="time_out2"
                label="Time Out 2"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <TimePicker format="hh:mm A" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12} className="default-bg p-2">
              <Row className="bold primary-color">
                {projectDetails?.project} Schedule
              </Row>
              {projectDetails && projectDetails.project_id && (
                <div className="">
                  <Row className="d-flex justify-content-between ms-3">
                    <Col className="smaller-text">
                      Time In 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timein,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                    <Col className="smaller-text">
                      Time Out 1:{" "}
                      <span className="bold">{`${moment(
                        projectDetails.first_timeout,
                        "h:mm A"
                      ).format("hh:mm A")}`}</span>
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-between ms-3">
                    {projectDetails.second_timein && (
                      <Col className="smaller-text">
                        Time In 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timein,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                    {projectDetails.second_timeout && (
                      <Col className="smaller-text">
                        Time Out 2:{" "}
                        <span className="bold">{`${moment(
                          projectDetails.second_timeout,
                          "h:mm A"
                        ).format("hh:mm A")}`}</span>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end mt-4">
              <Button
                danger
                onClick={() => setShowViewModal(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="text"
                onClick={() => {
                  setShowViewModal(false)
                  setShowEditModal(true)
                }}
                icon={<EditOutlined />}
              >
                Edit
              </Button>
              {isAdminLogin && (
                <Button
                  type="text"
                  danger
                  onClick={() => {
                    setShowConfirmDelete(true)
                    // confirmDelete(clickedRowData);
                  }}
                  icon={<DeleteOutlined />}
                >
                  Delete
                </Button>
              )}
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        footer={null}
        open={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
      >
        <div className="p-2">
          <Row>
            <Title level={5}>
              <ExclamationCircleOutlined /> Are you sure you want to delete this
              attendance?
            </Title>
          </Row>
          <Row className="ms-3 mt-1">You cannot undo this action.</Row>
          <Row className="ms-3">
            <Input
              placeholder="Enter reason for deletion"
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            />
          </Row>
          <Row className="mt-4 d-flex justify-content-end">
            <Space>
              <Button onClick={() => setShowConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                danger
                onClick={() => handleDelete(clickedRowData.id)}
                disabled={reason.trim() === ""}
              >
                Confirm
              </Button>
            </Space>
          </Row>
        </div>
      </Modal>

      <Modal
        footer={null}
        open={showConfirmMultipleDelete}
        onCancel={() => setShowConfirmMultipleDelete(false)}
      >
        <div className="p-2">
          <Row>
            <Title level={5}>
              <ExclamationCircleOutlined /> Are you sure you want to delete the
              selected timesheets?
            </Title>
          </Row>
          <Row className="ms-3 mt-1">You cannot undo this action.</Row>
          <Row className="ms-3">
            <Input
              placeholder="Enter reason for deletion"
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            />
          </Row>
          <Row className="mt-4 d-flex justify-content-end">
            <Space>
              <Button onClick={() => setShowConfirmMultipleDelete(false)}>
                Cancel
              </Button>
              <Button
                danger
                onClick={handleMultipleDelete}
                disabled={reason.trim() === ""}
              >
                Confirm
              </Button>
            </Space>
          </Row>
        </div>
      </Modal>

      {contextHolder}
    </Navbar>
  )
}
